import { Stack } from '@mui/material';
import Button from '@mui/material/Button';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../../api/http';
import {
	AutofillOperations,
	AutofillOperationTypes,
} from '../../../api/IntegrationsApi';
import PolicyApi from '../../../api/PolicyApi';
import { AuthData } from '../../../context/auth';
import { useConfigContext } from '../../../context/config';
import { ErrorData } from '../../../context/ErrorProvider';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import {
	POLICY_AUTOFILL_UPDATE_FAILURE,
	POLICY_AUTOFILL_UPDATE_SUCCESS,
	POLICY_DISCARD_FAILURE,
	POLICY_DISCARD_SUCCESS,
	POLICY_QUOTE_ACCEPT_FAILURE,
	POLICY_QUOTE_ACCEPT_SUCCESS,
	POLICY_QUOTE_CREATE_FAILURE,
	POLICY_QUOTE_CREATE_SUCCESS,
	POLICY_QUOTE_DISCARD_FAILURE,
	POLICY_QUOTE_DISCARD_SUCCESS,
	POLICY_QUOTE_FAILURE,
	POLICY_QUOTE_ISSUE_FAILURE,
	POLICY_QUOTE_ISSUE_SUCCESS,
	POLICY_QUOTE_NO_NAME_ACCEPT,
	POLICY_QUOTE_NO_NAME_CREATE,
	POLICY_QUOTE_NO_NAME_ISSUE,
	POLICY_QUOTE_NO_NAME_LOCK,
	POLICY_QUOTE_SUCCESS,
	POLICY_UPDATE_FAILURE,
	POLICY_UPDATE_SUCCESS,
} from '../../../data/labelsAndMessages';
import { isAutofillConfigured, isEmpty } from '../../../util/util';
import DropdownButton from '../../DropdownButton';
import { getDefaultPolicyValues } from '../../fastForm/formBuilders';
import { errorToast, successToast } from '../../Toast';
import ActionConfirmationModal from '../ActionConfirmationModal';
import RunAutofillButtonAndModal from '../RunAutofillButtonModal';

interface Props {
	policyData: PolicyData;
	authData: AuthData;
	errorData: ErrorData;
}

// Handles draft quotes and policies(application) without quotes enabled
const DraftPolicyQuoteActions = (props: Props) => {
	const navigate = useNavigate();
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields, isDirty } = formState;
	const configData = useConfigContext();
	const { authData, policyData, errorData } = props;
	const { hasPermission } = authData;
	const {
		selectedQuote,
		quotes,
		policy,
		quoteUpdateObj,
		setQuoteUpdateObj,
		policyUpdateObj,
		setPolicyUpdateObj,
		setLoading,
		saveFastForm,
	} = policyData;
	const { setCurrentErrorObject } = errorData;
	const setUpdateObject = selectedQuote
		? setQuoteUpdateObj
		: setPolicyUpdateObj;
	const autofillEnabled = isAutofillConfigured(
		policy.productName,
		AutofillOperations.NEW_BUSINESS,
		AutofillOperationTypes.UPDATE,
		configData?.dataAutofillSequences ?? [],
	);

	const onUpdate = async () => {
		setLoading(true);
		try {
			isChanged &&
				(await saveFastForm(getValues(), dirtyFields, reset, {
					runAutofill: false,
				}));
			successToast(POLICY_UPDATE_SUCCESS());
		} catch (error) {
			errorToast(POLICY_UPDATE_FAILURE());
			errorData.handleErrorResponse(error, POLICY_UPDATE_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		} finally {
			setLoading(false);
		}
	};

	const onRunAutofill = async () => {
		setLoading(true);
		try {
			await saveFastForm(getValues(), dirtyFields, reset, {
				runAutofill: true,
			});
			successToast(POLICY_AUTOFILL_UPDATE_SUCCESS());
		} catch (error) {
			errorToast(POLICY_AUTOFILL_UPDATE_FAILURE());
			errorData.handleErrorResponse(error, POLICY_AUTOFILL_UPDATE_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		} finally {
			setLoading(false);
		}
	};

	const onLock = async () => {
		// if quote name is empty, throw error
		if (!isEmpty(quoteUpdateObj) && quoteUpdateObj.name === '') {
			errorToast(POLICY_QUOTE_NO_NAME_LOCK());
			return;
		}
		setLoading(true);

		try {
			isChanged && (await saveFastForm(getValues(), dirtyFields, reset));
			if (selectedQuote) {
				await PolicyApi.quoteQuote(
					authData.auth.authorizationToken,
					selectedQuote.locator,
				);
				setQuoteUpdateObj({}); // reset update object
			} else {
				await PolicyApi.finalizePolicy(
					authData.auth.authorizationToken,
					policy.locator,
				);
				setPolicyUpdateObj({});
			}

			await policyData.loadData(policy.locator);
			setCurrentErrorObject(null);
			successToast(POLICY_QUOTE_SUCCESS());
		} catch (error) {
			errorToast(POLICY_QUOTE_FAILURE());
			errorData.handleErrorResponse(error, POLICY_QUOTE_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		}
		setLoading(false);
	};

	const createNewQuote = async () => {
		// if quote name is empty, throw error
		if (!isEmpty(quoteUpdateObj) && quoteUpdateObj.name === '') {
			errorToast(POLICY_QUOTE_NO_NAME_CREATE());
			return;
		}
		setLoading(true);
		const {
			authData: {
				auth: { authorizationToken },
			},
		} = props;
		try {
			isChanged && (await saveFastForm(getValues(), dirtyFields, reset));
			const newQuote = await PolicyApi.createNewQuote(
				authorizationToken,
				selectedQuote.locator,
			);
			setQuoteUpdateObj({}); // reset update obj
			await PolicyApi.selectQuote(authorizationToken, newQuote.data.locator);

			const updatedPolicyData = await policyData.loadData(policy.locator);
			const defaultPolicyValues = getDefaultPolicyValues(
				updatedPolicyData.fields,
				props.authData,
			);
			reset(defaultPolicyValues);

			navigate(`/policy/${policy.locator}/quotes/${newQuote.data.locator}`);
			successToast(POLICY_QUOTE_CREATE_SUCCESS());

			return;
		} catch (error) {
			errorToast(POLICY_QUOTE_CREATE_FAILURE());
			errorData.handleErrorResponse(error, POLICY_QUOTE_CREATE_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		} finally {
			setLoading(false);
		}
	};

	const discardQuote = async () => {
		setLoading(true);
		const {
			authData: {
				auth: { authorizationToken },
			},
		} = props;
		try {
			await PolicyApi.discardQuote(authorizationToken, selectedQuote.locator);
			const newSelectedQuote = quotes
				.filter((quote) => quote.locator !== selectedQuote.locator)
				.pop();
			await PolicyApi.selectQuote(authorizationToken, newSelectedQuote.locator);

			const updatedPolicyData = await policyData.loadData(policy.locator);
			const defaultPolicyValues = getDefaultPolicyValues(
				updatedPolicyData.fields,
				props.authData,
			);
			reset(defaultPolicyValues);
			navigate(`/policy/${policy.locator}/quotes/${newSelectedQuote.locator}`);
			successToast(POLICY_QUOTE_DISCARD_SUCCESS());
		} catch (error) {
			errorToast(POLICY_QUOTE_DISCARD_FAILURE());
			errorData.handleErrorResponse(error, POLICY_QUOTE_DISCARD_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		}
		setLoading(false);
	};

	const discardPolicy = async () => {
		setLoading(true);
		const {
			authData: {
				auth: { authorizationToken },
			},
		} = props;
		try {
			await PolicyApi.discardPolicy(authorizationToken, policy.locator);
			successToast(POLICY_DISCARD_SUCCESS());
		} catch (error) {
			errorToast(POLICY_DISCARD_FAILURE());
			errorData.handleErrorResponse(error, POLICY_DISCARD_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				setLoading(false);
				return;
			}
		}
		policyData.clear();
		navigate(`/policyholder/${policyData.policy.policyholderLocator}`);
		setLoading(false);
	};
	const isChanged =
		isDirty || !isEmpty(quoteUpdateObj) || !isEmpty(policyUpdateObj);

	const onIssueFromDraft = async () => {
		// if quote name is empty, throw error
		if (!isEmpty(quoteUpdateObj) && quoteUpdateObj.name === '') {
			errorToast(POLICY_QUOTE_NO_NAME_ISSUE());
			return;
		}
		setLoading(true);
		try {
			if (selectedQuote) {
				isChanged && (await saveFastForm(getValues(), dirtyFields, reset));
				await PolicyApi.quoteQuote(
					authData.auth.authorizationToken,
					selectedQuote.locator,
				);
				await PolicyApi.acceptQuote(
					authData.auth.authorizationToken,
					selectedQuote.locator,
				);
				await PolicyApi.issuePolicy(
					authData.auth.authorizationToken,
					policy.locator,
				);
			} else {
				if (!isEmpty(policyUpdateObj)) {
					await PolicyApi.updatePolicy(
						authData.auth.authorizationToken,
						policy.locator,
						policyUpdateObj,
					);
					setPolicyUpdateObj({});
				}
				await PolicyApi.finalizePolicy(
					authData.auth.authorizationToken,
					policy.locator,
				);
				await PolicyApi.issuePolicy(
					authData.auth.authorizationToken,
					policy.locator,
				);
			}
			setUpdateObject({});
			successToast(POLICY_QUOTE_ISSUE_SUCCESS());
		} catch (error) {
			errorToast(POLICY_QUOTE_ISSUE_FAILURE());
			errorData.handleErrorResponse(error, POLICY_QUOTE_ISSUE_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		} finally {
			await policyData.loadData(policy.locator);
			setLoading(false);
		}
	};

	const onAcceptFromDraft = async () => {
		// if quote name is empty, throw error
		if (!isEmpty(quoteUpdateObj) && quoteUpdateObj.name === '') {
			errorToast(POLICY_QUOTE_NO_NAME_ACCEPT());
			return;
		}
		setLoading(true);
		try {
			isChanged && (await saveFastForm(getValues(), dirtyFields, reset));
			await PolicyApi.quoteQuote(
				authData.auth.authorizationToken,
				selectedQuote.locator,
			);
			await PolicyApi.acceptQuote(
				authData.auth.authorizationToken,
				selectedQuote.locator,
			);
			await policyData.loadData(policy.locator);
			setQuoteUpdateObj({});
			successToast(POLICY_QUOTE_ACCEPT_SUCCESS());
		} catch (error) {
			errorToast(POLICY_QUOTE_ACCEPT_FAILURE());
			errorData.handleErrorResponse(error, POLICY_QUOTE_ACCEPT_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		} finally {
			setLoading(false);
		}
	};

	const hiddenOptions: any[] =
		selectedQuote &&
		hasPermission('quote.accept') &&
		hasPermission('quote.quote')
			? [{ label: 'Accept Policy', callback: onAcceptFromDraft }]
			: [];

	hasPermission('quote.quote') &&
		hasPermission('quote.accept') &&
		hasPermission('policy.update') &&
		hasPermission('policy.quote') &&
		hasPermission('policy.issue') &&
		hiddenOptions.push({
			label: 'Issue Policy',
			callback: null,
			component: (
				<ActionConfirmationModal
					label='Issue Policy'
					message='Are you sure you want to issue this quote?'
					confirmText='Issue'
					dropdownItem
					onConfirm={onIssueFromDraft}
				/>
			),
		});

	if (selectedQuote) {
		hasPermission('quote.create') &&
			hasPermission('quote.update') &&
			hasPermission('quote.quote') &&
			hiddenOptions.push({
				label: 'Create New Quote',
				callback: createNewQuote,
			});
		if (quotes.length > 1) {
			hasPermission('quote.discard') &&
				hasPermission('quote.quote') &&
				hiddenOptions.push({
					label: 'Discard Quote',
					callback: null,
					component: (
						<ActionConfirmationModal
							label='Discard Quote'
							message='Are you sure you want to discard this quote?'
							confirmText='Discard Quote'
							dropdownItem
							onConfirm={discardQuote}
						/>
					),
				});
		} else {
			hasPermission('policy.discard') &&
				hiddenOptions.push({
					label: 'Discard Policy',
					callback: null,
					component: (
						<ActionConfirmationModal
							label='Discard Policy'
							message='Are you sure you want to discard this policy?'
							confirmText='Discard Policy'
							dropdownItem
							onConfirm={discardPolicy}
						/>
					),
				});
		}
	}

	if (!selectedQuote) {
		hasPermission('policy.discard') &&
			hiddenOptions.push({
				label: 'Discard Policy',
				callback: null,
				component: (
					<ActionConfirmationModal
						label='Discard Policy'
						message='Are you sure you want to discard this policy?'
						confirmText='Discard Policy'
						dropdownItem
						onConfirm={discardPolicy}
					/>
				),
			});
	}

	const buttons = (
		<Stack direction='row' spacing={1}>
			{autofillEnabled && hasPermission('quote.update') ? (
				<RunAutofillButtonAndModal
					onConfirm={onRunAutofill}
					disabled={!isChanged}
				/>
			) : null}
			{hasPermission('quote.update') && (
				<Button
					color='primary'
					disabled={!isChanged}
					onClick={isChanged ? onUpdate : null}
					variant='contained'
				>
					Save
				</Button>
			)}
			{hasPermission('quote.quote') &&
				hasPermission('quote.update') &&
				hasPermission('policy.quote') && (
					<Button
						key={'LockAndPrice'}
						color='primary'
						onClick={onLock}
						variant='contained'
					>
						{selectedQuote ? 'Lock and Price' : 'Finalize Policy'}
					</Button>
				)}
			<DropdownButton actions={hiddenOptions} />
		</Stack>
	);

	return buttons;
};

export default withPolicyContext<Props>(DraftPolicyQuoteActions);
